import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {Menu, X, Car, User} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

interface NavbarProps {
  onAuthClick: () => void
}

export default function Navbar({ onAuthClick }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { user, isAuthenticated, isAdmin, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (location.pathname === '/') {
      // Scroll vers la section contact
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
    } else {
      // Naviguer vers l'accueil puis scroll
      navigate('/')
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
    setIsOpen(false)
  }

  const handleSignOut = async () => {
    await signOut()
    setIsOpen(false)
  }

  return (
    <nav className="bg-black text-white sticky top-0 z-50 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <Car className="w-8 h-8 text-[#fbbf24]" />
            <span className="text-2xl font-bold">Ady Garage</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="hover:text-[#fbbf24] transition-colors">
              Accueil
            </Link>
            <Link to="/flotte" className="hover:text-[#fbbf24] transition-colors">
              Location
            </Link>
            <a href="https://app.adysarl.ch/" target="_blank" rel="noopener noreferrer" className="hover:text-[#fbbf24] transition-colors">
              Fidélité
            </a>
            <a href="#contact" onClick={handleContactClick} className="hover:text-[#fbbf24] transition-colors cursor-pointer">
              Contact
            </a>

            {isAuthenticated ? (
              <>
                <Link to="/mes-reservations" className="hover:text-[#fbbf24] transition-colors">
                  Mes réservations
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="hover:text-[#fbbf24] transition-colors">
                    Admin
                  </Link>
                )}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-[#fbbf24]" />
                    <span className="text-sm">{user?.userName}</span>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="text-sm hover:text-[#fbbf24] transition-colors"
                  >
                    Déconnexion
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={onAuthClick}
                className="flex items-center gap-2 hover:text-[#fbbf24] transition-colors"
              >
                <User className="w-5 h-5" />
                <span>Connexion</span>
              </button>
            )}

            <Link
              to="/flotte"
              className="bg-[#fbbf24] text-black px-6 py-2 rounded-lg hover:bg-[#f59e0b] transition-all hover:scale-105"
            >
              Réserver
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-900 transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 space-y-3 border-t border-gray-700">
            <Link
              to="/"
              className="block py-2 hover:text-[#fbbf24] transition-colors"
              onClick={() => setIsOpen(false)}
            >
              Accueil
            </Link>
            <Link
              to="/flotte"
              className="block py-2 hover:text-[#fbbf24] transition-colors"
              onClick={() => setIsOpen(false)}
            >
              Location
            </Link>
            <a
              href="https://app.adysarl.ch/"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-2 hover:text-[#fbbf24] transition-colors"
              onClick={() => setIsOpen(false)}
            >
              Fidélité
            </a>
            <a
              href="#contact"
              className="block py-2 hover:text-[#fbbf24] transition-colors cursor-pointer"
              onClick={handleContactClick}
            >
              Contact
            </a>

            {isAuthenticated ? (
              <>
                <Link
                  to="/mes-reservations"
                  className="block py-2 hover:text-[#fbbf24] transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  Mes réservations
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="block py-2 hover:text-[#fbbf24] transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    Admin
                  </Link>
                )}
                <div className="py-2 border-t border-gray-700 mt-2 pt-3">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-[#fbbf24]" />
                    <span className="text-sm">{user?.userName}</span>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="text-sm hover:text-[#fbbf24] transition-colors"
                  >
                    Déconnexion
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={() => {
                  onAuthClick()
                  setIsOpen(false)
                }}
                className="flex items-center gap-2 py-2 hover:text-[#fbbf24] transition-colors"
              >
                <User className="w-5 h-5" />
                <span>Connexion</span>
              </button>
            )}

            <Link
              to="/flotte"
              className="block bg-[#fbbf24] text-black px-6 py-2 rounded-lg hover:bg-[#f59e0b] transition-colors text-center mt-3"
              onClick={() => setIsOpen(false)}
            >
              Réserver
            </Link>
          </div>
        )}
      </div>
    </nav>
  )
}
